// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak-fork/web';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { teal } from 'api';
import { getUser } from 'actions/auth';
import Content from 'components/Content';
import Section from 'components/Section';
import paths from 'paths';
import { ORDER_STATUS_FLOW_FEATURE } from 'utils/constants';
import { hasOrganisationAdminAccess } from './organisationAdminAccess';

const ORGANISATION_ROLES = ['Admin', 'Viewer', 'User'];
const AVAILABLE_ENABLED_FEATURES = [
    { value: 'access-orders', name: 'Access orders' },
    { value: 'PenlyAccessInvite', name: 'Penly Access invite' },
    { value: ORDER_STATUS_FLOW_FEATURE, name: 'Ny ordrestatus (opfølgning, faktura, ikke solgt)' },
];
const defaultOrganisationForm = { name: '', cvr: '', domain: '', verified: true, active: true, enabledFeaturesText: '' };
const defaultMemberForm = { email: '', firstName: '', lastName: '', role: 'User' };

function sortByName(items, getValue) {
    return [...(items || [])].sort((left, right) => {
        const leftValue = (getValue(left) || '').toLowerCase();
        const rightValue = (getValue(right) || '').toLowerCase();
        return leftValue.localeCompare(rightValue, 'da');
    });
}

function formatErrorMessage(error, fallbackMessage) {
    return error?.response?.data?.reason
        || error?.response?.data?.error
        || error?.message
        || fallbackMessage;
}

function normalizeOrganisationForm(organisation) {
    if (!organisation) return { ...defaultOrganisationForm };

    return {
        name: organisation.name || '',
        cvr: organisation.cvr || organisation.vatin || '',
        domain: organisation.domain || '',
        verified: organisation.verified !== false,
        active: organisation.active !== false,
        enabledFeaturesText: (organisation.enabledFeatures || []).join(', '),
    };
}

function parseEnabledFeatures(value) {
    return [...new Set(
        (value || '')
            .split(/[\n,]+/)
            .map(item => item.trim())
            .filter(Boolean),
    )];
}

function formatEnabledFeatures(features) {
    return [...new Set((features || []).filter(Boolean))].join(', ');
}

function toggleEnabledFeature(currentValue, featureValue, isEnabled) {
    const nextFeatures = parseEnabledFeatures(currentValue).filter(feature => feature !== featureValue);

    if (isEnabled) {
        nextFeatures.push(featureValue);
    }

    return formatEnabledFeatures(nextFeatures);
}

function getCustomEnabledFeaturesText(value) {
    const knownFeatureValues = AVAILABLE_ENABLED_FEATURES.map(feature => feature.value);

    return formatEnabledFeatures(
        parseEnabledFeatures(value).filter(feature => !knownFeatureValues.includes(feature)),
    );
}

function buildOrganisationPayload(formValues) {
    return {
        name: formValues.name.trim(),
        cvr: formValues.cvr.trim(),
        domain: formValues.domain.trim() || undefined,
        verified: Boolean(formValues.verified),
        active: Boolean(formValues.active),
        enabledFeatures: parseEnabledFeatures(formValues.enabledFeaturesText),
    };
}

function normalizeAdminUser(user) {
    return {
        ...user,
        userId: user?.userId || user?.id,
    };
}

function formatUserName(user) {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.email || user?.userId || 'Ukendt bruger';
}

function getOrganisationUserLabel(user, fallbackUser = {}) {
    return formatUserName({
        firstName: fallbackUser?.firstName || user?.firstName,
        lastName: fallbackUser?.lastName || user?.lastName,
        email: fallbackUser?.email || user?.email,
        userId: fallbackUser?.userId || fallbackUser?.id || user?.userId || user?.id,
    });
}

function formatOrganisationStatus(organisation) {
    if (!organisation) return '-';
    if (!organisation.active) return 'Inaktiv';
    if (!organisation.verified) return 'Afventer godkendelse';
    return 'Aktiv';
}

const Organisation = () => {
    const dispatch = useDispatch();
    const { keycloak } = useKeycloak();
    const { organisationId: routeOrganisationId } = useParams();
    const user = useSelector(state => state.auth.user);

    const canAccessOrganisationAdmin = hasOrganisationAdminAccess({ tokenParsed: keycloak?.tokenParsed, user });

    const [organisations, setOrganisations] = useState([]);
    const [selectedOrganisationId, setSelectedOrganisationId] = useState(routeOrganisationId || '');
    const [selectedOrganisation, setSelectedOrganisation] = useState(null);
    const [selectedOrganisationForm, setSelectedOrganisationForm] = useState({ ...defaultOrganisationForm });
    const [organisationUsers, setOrganisationUsers] = useState([]);
    const [memberRoleDrafts, setMemberRoleDrafts] = useState({});

    const [listLoading, setListLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [detailsError, setDetailsError] = useState('');

    const [createForm, setCreateForm] = useState({ ...defaultOrganisationForm });
    const [createError, setCreateError] = useState('');
    const [memberForm, setMemberForm] = useState({ ...defaultMemberForm });
    const [memberError, setMemberError] = useState('');

    const [isCreatingOrganisation, setIsCreatingOrganisation] = useState(false);
    const [isUpdatingOrganisation, setIsUpdatingOrganisation] = useState(false);
    const [isDeletingOrganisation, setIsDeletingOrganisation] = useState(false);
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [updatingMemberId, setUpdatingMemberId] = useState('');
    const [deletingMemberId, setDeletingMemberId] = useState('');

    const [snack, setSnack] = useState(null);

    const organisationRows = useMemo(
        () => sortByName(organisations, organisation => organisation?.name),
        [organisations],
    );

    const loadOrganisations = useCallback(async ({ preferredOrganisationId, preserveCurrentSelection = true } = {}) => {
        setListLoading(true);
        setListError('');

        try {
            const organisationsResponse = await teal.getAdminOrganisations();
            const nextOrganisations = sortByName(organisationsResponse, organisation => organisation?.name);
            const preferredId = preferredOrganisationId
                || (preserveCurrentSelection ? selectedOrganisationId : '')
                || routeOrganisationId;
            const fallbackId = nextOrganisations[0]?.organisationId || '';
            const nextSelectedOrganisationId = preferredId && nextOrganisations.some(org => org.organisationId === preferredId)
                ? preferredId
                : fallbackId;

            setOrganisations(nextOrganisations);
            setSelectedOrganisationId(nextSelectedOrganisationId);
        } catch (error) {
            setListError(formatErrorMessage(error, 'Kunne ikke hente organisationer'));
        } finally {
            setListLoading(false);
        }
    }, [routeOrganisationId, selectedOrganisationId]);

    const loadOrganisationDetails = useCallback(async (organisationId) => {
        if (!organisationId) {
            setSelectedOrganisation(null);
            setSelectedOrganisationForm({ ...defaultOrganisationForm });
            setOrganisationUsers([]);
            setMemberRoleDrafts({});
            return;
        }

        setDetailsLoading(true);
        setDetailsError('');

        try {
            const [organisationResponse, usersResponse] = await Promise.all([
                teal.getAdminOrganisation(organisationId),
                teal.getAdminOrganisationUsers(organisationId),
            ]);

            const normalizedUsers = sortByName(
                (usersResponse || []).map(normalizeAdminUser),
                organisationUser => formatUserName(organisationUser),
            );

            setSelectedOrganisation(organisationResponse);
            setSelectedOrganisationForm(normalizeOrganisationForm(organisationResponse));
            setOrganisationUsers(normalizedUsers);
            setMemberRoleDrafts(
                normalizedUsers.reduce((accumulator, organisationUser) => ({
                    ...accumulator,
                    [organisationUser.userId]: organisationUser.role || 'User',
                }), {}),
            );
        } catch (error) {
            setDetailsError(formatErrorMessage(error, 'Kunne ikke hente organisationsdetaljer'));
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!canAccessOrganisationAdmin) return;
        loadOrganisations({ preserveCurrentSelection: true });
    }, [canAccessOrganisationAdmin, loadOrganisations]);

    useEffect(() => {
        if (!canAccessOrganisationAdmin) return;
        loadOrganisationDetails(selectedOrganisationId);
    }, [canAccessOrganisationAdmin, loadOrganisationDetails, selectedOrganisationId]);

    const refreshCurrentUser = useCallback(async () => {
        try {
            await dispatch(getUser());
        } catch {
            // ignore user refresh failures here - the admin action itself already succeeded
        }
    }, [dispatch]);

    const handleSelectOrganisation = (organisationId) => {
        setSelectedOrganisationId(organisationId);
    };

    const handleCreateOrganisationFieldChange = (field) => (event) => {
        const { target } = event;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        setCreateForm(previousState => ({ ...previousState, [field]: value }));
    };

    const handleCreateEnabledFeatureToggle = (featureValue) => (event) => {
        setCreateForm(previousState => ({
            ...previousState,
            enabledFeaturesText: toggleEnabledFeature(previousState.enabledFeaturesText, featureValue, event.target.checked),
        }));
    };

    const handleCreateCustomEnabledFeaturesChange = (event) => {
        setCreateForm(previousState => {
            const selectedKnownFeatures = parseEnabledFeatures(previousState.enabledFeaturesText)
                .filter(feature => AVAILABLE_ENABLED_FEATURES.some(option => option.value === feature));

            return {
                ...previousState,
                enabledFeaturesText: formatEnabledFeatures([
                    ...selectedKnownFeatures,
                    ...parseEnabledFeatures(event.target.value),
                ]),
            };
        });
    };

    const handleSelectedEnabledFeatureToggle = (featureValue) => (event) => {
        setSelectedOrganisationForm(previousState => ({
            ...previousState,
            enabledFeaturesText: toggleEnabledFeature(previousState.enabledFeaturesText, featureValue, event.target.checked),
        }));
    };

    const handleSelectedCustomEnabledFeaturesChange = (event) => {
        setSelectedOrganisationForm(previousState => {
            const selectedKnownFeatures = parseEnabledFeatures(previousState.enabledFeaturesText)
                .filter(feature => AVAILABLE_ENABLED_FEATURES.some(option => option.value === feature));

            return {
                ...previousState,
                enabledFeaturesText: formatEnabledFeatures([
                    ...selectedKnownFeatures,
                    ...parseEnabledFeatures(event.target.value),
                ]),
            };
        });
    };

    const handleSelectedOrganisationFieldChange = (field) => (event) => {
        const { target } = event;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        setSelectedOrganisationForm(previousState => ({ ...previousState, [field]: value }));
    };

    const handleMemberFieldChange = (field) => (event) => {
        const { target } = event;
        const { value } = target;
        setMemberForm(previousState => ({ ...previousState, [field]: value }));
    };

    const handleCreateOrganisation = async (event) => {
        event.preventDefault();
        setCreateError('');

        if (!createForm.name.trim()) {
            setCreateError('Virksomhedsnavn er påkrævet');
            return;
        }

        if (!createForm.cvr.trim()) {
            setCreateError('CVR-nummer er påkrævet');
            return;
        }

        setIsCreatingOrganisation(true);

        try {
            const organisation = await teal.createAdminOrganisation(buildOrganisationPayload(createForm));
            await refreshCurrentUser();
            await loadOrganisations({ preferredOrganisationId: organisation.organisationId, preserveCurrentSelection: false });
            setCreateForm({ ...defaultOrganisationForm });
            setSnack({ severity: 'success', message: `${organisation.name} blev oprettet` });
        } catch (error) {
            setCreateError(formatErrorMessage(error, 'Kunne ikke oprette organisation'));
        } finally {
            setIsCreatingOrganisation(false);
        }
    };

    const handleUpdateOrganisation = async (event) => {
        event.preventDefault();
        setDetailsError('');

        if (!selectedOrganisationId) return;

        if (!selectedOrganisationForm.name.trim()) {
            setDetailsError('Virksomhedsnavn er påkrævet');
            return;
        }

        if (!selectedOrganisationForm.cvr.trim()) {
            setDetailsError('CVR-nummer er påkrævet');
            return;
        }

        setIsUpdatingOrganisation(true);

        try {
            const organisation = await teal.updateAdminOrganisation(
                selectedOrganisationId,
                buildOrganisationPayload(selectedOrganisationForm),
            );
            await loadOrganisations({ preferredOrganisationId: organisation.organisationId, preserveCurrentSelection: false });
            await loadOrganisationDetails(organisation.organisationId);
            setSnack({ severity: 'success', message: `${organisation.name} blev opdateret` });
        } catch (error) {
            setDetailsError(formatErrorMessage(error, 'Kunne ikke opdatere organisation'));
        } finally {
            setIsUpdatingOrganisation(false);
        }
    };

    const handleDeleteOrganisation = async () => {
        if (!selectedOrganisationId || !selectedOrganisation) return;

        const confirmed = window.confirm(`Er du sikker på at du vil slette ${selectedOrganisation.name}?`); // eslint-disable-line no-alert
        if (!confirmed) return;

        setDetailsError('');
        setIsDeletingOrganisation(true);

        try {
            await teal.deleteAdminOrganisation(selectedOrganisationId);
            await refreshCurrentUser();
            await loadOrganisations({ preserveCurrentSelection: false });
            setSnack({ severity: 'success', message: `${selectedOrganisation.name} blev slettet` });
        } catch (error) {
            setDetailsError(formatErrorMessage(error, 'Kunne ikke slette organisation'));
        } finally {
            setIsDeletingOrganisation(false);
        }
    };

    const handleAddMember = async (event) => {
        event.preventDefault();
        setMemberError('');

        if (!selectedOrganisationId) return;

        const submittedMember = {
            email: memberForm.email.trim(),
            firstName: memberForm.firstName.trim(),
            lastName: memberForm.lastName.trim(),
            role: memberForm.role,
        };

        if (!submittedMember.email) {
            setMemberError('E-mail er påkrævet');
            return;
        }

        setIsAddingMember(true);

        try {
            const organisationUser = await teal.assignAdminOrganisationUser(selectedOrganisationId, submittedMember);
            const organisationUserLabel = getOrganisationUserLabel(organisationUser, submittedMember);

            await refreshCurrentUser();
            await loadOrganisationDetails(selectedOrganisationId);
            setMemberForm({ ...defaultMemberForm });
            setSnack({
                severity: 'success',
                message: organisationUser?.invited
                    ? `${organisationUserLabel} blev inviteret og tilføjet til organisationen`
                    : `${organisationUserLabel} blev tilføjet til organisationen`,
            });
        } catch (error) {
            setMemberError(formatErrorMessage(error, 'Kunne ikke tilføje organisationsmedlem'));
        } finally {
            setIsAddingMember(false);
        }
    };

    const handleRoleDraftChange = (userId) => (event) => {
        setMemberRoleDrafts(previousState => ({
            ...previousState,
            [userId]: event.target.value,
        }));
    };

    const handleUpdateMemberRole = async (organisationUser) => {
        if (!selectedOrganisationId || !organisationUser?.userId) return;

        const role = memberRoleDrafts[organisationUser.userId] || organisationUser.role;
        setUpdatingMemberId(organisationUser.userId);
        setMemberError('');

        try {
            await teal.updateAdminOrganisationUser(selectedOrganisationId, organisationUser.userId, { role });
            await refreshCurrentUser();
            await loadOrganisationDetails(selectedOrganisationId);
            setSnack({ severity: 'success', message: `${formatUserName(organisationUser)} fik rollen ${role}` });
        } catch (error) {
            setMemberError(formatErrorMessage(error, 'Kunne ikke opdatere organisationsmedlem'));
        } finally {
            setUpdatingMemberId('');
        }
    };

    const handleRemoveMember = async (organisationUser) => {
        if (!selectedOrganisationId || !organisationUser?.userId) return;

        const confirmed = window.confirm(`Fjern ${formatUserName(organisationUser)} fra organisationen?`); // eslint-disable-line no-alert
        if (!confirmed) return;

        setDeletingMemberId(organisationUser.userId);
        setMemberError('');

        try {
            await teal.deleteAdminOrganisationUser(selectedOrganisationId, organisationUser.userId);
            await refreshCurrentUser();
            await loadOrganisationDetails(selectedOrganisationId);
            setSnack({ severity: 'success', message: `${formatUserName(organisationUser)} blev fjernet fra organisationen` });
        } catch (error) {
            setMemberError(formatErrorMessage(error, 'Kunne ikke fjerne organisationsmedlem'));
        } finally {
            setDeletingMemberId('');
        }
    };

    if (!canAccessOrganisationAdmin) {
        return <Navigate to={ routeOrganisationId ? paths.penlyAccess.getPath(routeOrganisationId) : paths.organisations.getPath() } replace />;
    }

    return (
        <Content>
            <Section
                title="Organisation administration"
                description="Administrér organisationer, medlemskaber og inviter nye brugere via den nye admin-API."
            >
                <Stack spacing={ 3 }>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Kun brugere med <strong>teal:admin</strong> eller allowlistet e-mail kan se denne side.
                        </Typography>
                        <Button variant="outlined" onClick={ () => loadOrganisations({ preserveCurrentSelection: true }) } disabled={ listLoading || detailsLoading }>
                            Genindlæs
                        </Button>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(320px, 1fr) minmax(0, 2fr)' }, gap: 3 }}>
                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h6">Organisationer</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Vælg en organisation for at redigere data og medlemskaber.
                                </Typography>
                            </Box>

                            { listError && <Alert severity="error" sx={{ m: 2 }}>{ listError }</Alert> }

                            { listLoading ? (
                                <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size={ 32 } />
                                </Box>
                            ) : organisationRows.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Alert severity="info">Der er ingen organisationer endnu.</Alert>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Navn</TableCell>
                                                <TableCell>CVR</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="right" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            { organisationRows.map(organisation => {
                                                const isSelected = organisation.organisationId === selectedOrganisationId;

                                                return (
                                                    <TableRow
                                                        key={ organisation.organisationId }
                                                        hover={ true }
                                                        selected={ isSelected }
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={ () => handleSelectOrganisation(organisation.organisationId) }
                                                    >
                                                        <TableCell>
                                                            <Box>
                                                                <Typography fontWeight={ isSelected ? 700 : 500 }>{ organisation.name }</Typography>
                                                                <Typography variant="caption" color="text.secondary">{ organisation.organisationId }</Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{ organisation.cvr || '-' }</TableCell>
                                                        <TableCell>{ formatOrganisationStatus(organisation) }</TableCell>
                                                        <TableCell align="right">
                                                            <Button size="small" variant={ isSelected ? 'contained' : 'text' }>
                                                                { isSelected ? 'Valgt' : 'Vælg' }
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }) }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) }
                        </Paper>

                        <Stack spacing={ 3 }>
                            <Paper variant="outlined" sx={{ p: 2.5 }}>
                                <Typography variant="h6" sx={{ mb: 1.5 }}>Valgt organisation</Typography>

                                { detailsError && <Alert severity="error" sx={{ mb: 2 }}>{ detailsError }</Alert> }

                                { detailsLoading && !selectedOrganisation ? (
                                    <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress size={ 32 } />
                                    </Box>
                                ) : !selectedOrganisation ? (
                                    <Alert severity="info">Vælg en organisation for at se detaljer.</Alert>
                                ) : (
                                    <Stack spacing={ 2.5 }>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Alert severity={ selectedOrganisation.active ? 'success' : 'warning' } sx={{ py: 0 }}>{ formatOrganisationStatus(selectedOrganisation) }</Alert>
                                            { selectedOrganisation.updatedAt && (
                                                <Alert severity="info" sx={{ py: 0 }}>
                                                    Sidst opdateret: { new Date(selectedOrganisation.updatedAt).toLocaleString('da-DK') }
                                                </Alert>
                                            ) }
                                        </Box>

                                        <Box component="form" onSubmit={ handleUpdateOrganisation }>
                                            <Stack spacing={ 2 }>
                                                <TextField
                                                    label="Virksomhedsnavn"
                                                    value={ selectedOrganisationForm.name }
                                                    onChange={ handleSelectedOrganisationFieldChange('name') }
                                                    fullWidth={ true }
                                                />
                                                <TextField
                                                    label="CVR-nummer"
                                                    value={ selectedOrganisationForm.cvr }
                                                    onChange={ handleSelectedOrganisationFieldChange('cvr') }
                                                    fullWidth={ true }
                                                />
                                                <TextField
                                                    label="Domæne"
                                                    value={ selectedOrganisationForm.domain }
                                                    onChange={ handleSelectedOrganisationFieldChange('domain') }
                                                    placeholder="example.com"
                                                    fullWidth={ true }
                                                />
                                                <FormControl component="fieldset" variant="standard">
                                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Enabled features</Typography>
                                                    <FormHelperText sx={{ mt: 0, mb: 1.5 }}>Se og opdatér organisationens kendte features her.</FormHelperText>
                                                    <FormGroup sx={{ gap: 1 }}>
                                                        { AVAILABLE_ENABLED_FEATURES.map(feature => (
                                                            <FormControlLabel
                                                                key={ feature.value }
                                                                control={ (
                                                                    <Checkbox
                                                                        checked={ parseEnabledFeatures(selectedOrganisationForm.enabledFeaturesText).includes(feature.value) }
                                                                        onChange={ handleSelectedEnabledFeatureToggle(feature.value) }
                                                                    />
                                                                ) }
                                                                label={ (
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{ feature.name }</Typography>
                                                                        <Typography variant="caption" color="text.secondary">{ feature.value }</Typography>
                                                                    </Box>
                                                                ) }
                                                            />
                                                        )) }
                                                    </FormGroup>
                                                </FormControl>
                                                <TextField
                                                    label="Andre enabled features (avanceret)"
                                                    value={ getCustomEnabledFeaturesText(selectedOrganisationForm.enabledFeaturesText) }
                                                    onChange={ handleSelectedCustomEnabledFeaturesChange }
                                                    helperText="Tilføj eller behold ekstra feature-flags, som ikke er på listen ovenfor."
                                                    multiline={ true }
                                                    minRows={ 2 }
                                                    fullWidth={ true }
                                                />
                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                    <FormControlLabel
                                                        control={ <Checkbox checked={ selectedOrganisationForm.verified } onChange={ handleSelectedOrganisationFieldChange('verified') } /> }
                                                        label="Godkendt"
                                                    />
                                                    <FormControlLabel
                                                        control={ <Checkbox checked={ selectedOrganisationForm.active } onChange={ handleSelectedOrganisationFieldChange('active') } /> }
                                                        label="Aktiv"
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                    <Button type="submit" variant="contained" disabled={ isUpdatingOrganisation || isDeletingOrganisation }>
                                                        { isUpdatingOrganisation ? 'Gemmer...' : 'Gem ændringer' }
                                                    </Button>
                                                    <Button color="error" variant="outlined" onClick={ handleDeleteOrganisation } disabled={ isUpdatingOrganisation || isDeletingOrganisation }>
                                                        { isDeletingOrganisation ? 'Sletter...' : 'Slet organisation' }
                                                    </Button>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                ) }
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5 }}>
                                <Typography variant="h6" sx={{ mb: 1.5 }}>Medlemmer</Typography>

                                { memberError && <Alert severity="error" sx={{ mb: 2 }}>{ memberError }</Alert> }

                                { !selectedOrganisation ? (
                                    <Alert severity="info">Vælg en organisation før du administrerer medlemmer.</Alert>
                                ) : (
                                    <Stack spacing={ 2.5 }>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Bruger</TableCell>
                                                        <TableCell>E-mail</TableCell>
                                                        <TableCell>Rolle</TableCell>
                                                        <TableCell align="right">Handlinger</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    { organisationUsers.length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={ 4 }>
                                                                <Alert severity="info">Organisationen har ingen medlemmer endnu.</Alert>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) }
                                                    { organisationUsers.map(organisationUser => {
                                                        const isUpdatingUser = updatingMemberId === organisationUser.userId;
                                                        const isDeletingUser = deletingMemberId === organisationUser.userId;
                                                        const selectedRole = memberRoleDrafts[organisationUser.userId] || organisationUser.role || 'User';
                                                        const roleChanged = selectedRole !== (organisationUser.role || 'User');

                                                        return (
                                                            <TableRow key={ organisationUser.userId }>
                                                                <TableCell>{ formatUserName(organisationUser) }</TableCell>
                                                                <TableCell>{ organisationUser.email || '-' }</TableCell>
                                                                <TableCell sx={{ minWidth: 150 }}>
                                                                    <FormControl fullWidth={ true } size="small">
                                                                        <InputLabel id={ `member-role-${organisationUser.userId}` }>Rolle</InputLabel>
                                                                        <Select
                                                                            labelId={ `member-role-${organisationUser.userId}` }
                                                                            label="Rolle"
                                                                            value={ selectedRole }
                                                                            onChange={ handleRoleDraftChange(organisationUser.userId) }
                                                                        >
                                                                            { ORGANISATION_ROLES.map(role => (
                                                                                <MenuItem key={ role } value={ role }>{ role }</MenuItem>
                                                                            )) }
                                                                        </Select>
                                                                    </FormControl>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                                        <Button
                                                                            type="button"
                                                                            size="small"
                                                                            variant="contained"
                                                                            onClick={ () => handleUpdateMemberRole(organisationUser) }
                                                                            disabled={ !roleChanged || isUpdatingUser || isDeletingUser }
                                                                        >
                                                                            { isUpdatingUser ? 'Gemmer...' : 'Gem rolle' }
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            size="small"
                                                                            color="error"
                                                                            variant="outlined"
                                                                            onClick={ () => handleRemoveMember(organisationUser) }
                                                                            disabled={ isUpdatingUser || isDeletingUser }
                                                                        >
                                                                            { isDeletingUser ? 'Fjerner...' : 'Fjern' }
                                                                        </Button>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }) }
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Box component="form" onSubmit={ handleAddMember }>
                                            <Stack spacing={ 2 }>
                                                <Typography variant="subtitle1">Tilføj eller invitér bruger</Typography>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 180px' }, gap: 2 }}>
                                                    <TextField
                                                        label="E-mail"
                                                        value={ memberForm.email }
                                                        onChange={ handleMemberFieldChange('email') }
                                                        placeholder="bruger@example.com"
                                                        fullWidth={ true }
                                                    />
                                                    <TextField
                                                        label="Fornavn"
                                                        value={ memberForm.firstName }
                                                        onChange={ handleMemberFieldChange('firstName') }
                                                        fullWidth={ true }
                                                    />
                                                    <TextField
                                                        label="Efternavn"
                                                        value={ memberForm.lastName }
                                                        onChange={ handleMemberFieldChange('lastName') }
                                                        fullWidth={ true }
                                                    />
                                                    <FormControl fullWidth={ true }>
                                                        <InputLabel id="new-member-role">Rolle</InputLabel>
                                                        <Select
                                                            labelId="new-member-role"
                                                            label="Rolle"
                                                            value={ memberForm.role }
                                                            onChange={ handleMemberFieldChange('role') }
                                                        >
                                                            { ORGANISATION_ROLES.map(role => (
                                                                <MenuItem key={ role } value={ role }>{ role }</MenuItem>
                                                            )) }
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <Button type="submit" variant="contained" disabled={ isAddingMember }>
                                                        { isAddingMember ? 'Tilføjer...' : 'Tilføj / invitér bruger' }
                                                    </Button>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                ) }
                            </Paper>
                        </Stack>
                    </Box>

                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>Opret organisation</Typography>
                        { createError && <Alert severity="error" sx={{ mb: 2 }}>{ createError }</Alert> }

                        <Box component="form" onSubmit={ handleCreateOrganisation }>
                            <Stack spacing={ 2 }>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
                                    <TextField
                                        label="Nyt virksomhedsnavn"
                                        value={ createForm.name }
                                        onChange={ handleCreateOrganisationFieldChange('name') }
                                        fullWidth={ true }
                                    />
                                    <TextField
                                        label="Nyt CVR-nummer"
                                        value={ createForm.cvr }
                                        onChange={ handleCreateOrganisationFieldChange('cvr') }
                                        fullWidth={ true }
                                    />
                                    <TextField
                                        label="Nyt domæne"
                                        value={ createForm.domain }
                                        onChange={ handleCreateOrganisationFieldChange('domain') }
                                        placeholder="example.com"
                                        fullWidth={ true }
                                    />
                                </Box>
                                <FormControl component="fieldset" variant="standard">
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Enabled features</Typography>
                                    <FormHelperText sx={{ mt: 0, mb: 1.5 }}>Vælg de features organisationen skal have fra start.</FormHelperText>
                                    <FormGroup sx={{ gap: 1 }}>
                                        { AVAILABLE_ENABLED_FEATURES.map(feature => (
                                            <FormControlLabel
                                                key={ feature.value }
                                                control={ (
                                                    <Checkbox
                                                        checked={ parseEnabledFeatures(createForm.enabledFeaturesText).includes(feature.value) }
                                                        onChange={ handleCreateEnabledFeatureToggle(feature.value) }
                                                    />
                                                ) }
                                                label={ (
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{ feature.name }</Typography>
                                                        <Typography variant="caption" color="text.secondary">{ feature.value }</Typography>
                                                    </Box>
                                                ) }
                                            />
                                        )) }
                                    </FormGroup>
                                </FormControl>
                                <TextField
                                    label="Andre enabled features (avanceret)"
                                    value={ getCustomEnabledFeaturesText(createForm.enabledFeaturesText) }
                                    onChange={ handleCreateCustomEnabledFeaturesChange }
                                    helperText="Tilføj ekstra feature-flags med komma eller linjeskift, hvis de ikke er på listen ovenfor."
                                    multiline={ true }
                                    minRows={ 2 }
                                    fullWidth={ true }
                                />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <FormControlLabel
                                        control={ <Checkbox checked={ createForm.verified } onChange={ handleCreateOrganisationFieldChange('verified') } /> }
                                        label="Godkendt"
                                    />
                                    <FormControlLabel
                                        control={ <Checkbox checked={ createForm.active } onChange={ handleCreateOrganisationFieldChange('active') } /> }
                                        label="Aktiv"
                                    />
                                </Box>
                                <Box>
                                    <Button type="submit" variant="contained" disabled={ isCreatingOrganisation }>
                                        { isCreatingOrganisation ? 'Opretter...' : 'Opret organisation' }
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    </Paper>
                </Stack>
            </Section>

            <Snackbar
                open={ Boolean(snack) }
                autoHideDuration={ 5000 }
                onClose={ () => setSnack(null) }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={ snack?.severity || 'success' } onClose={ () => setSnack(null) }>
                    { snack?.message }
                </Alert>
            </Snackbar>
        </Content>
    );
};

export default Organisation;
